import { type InferOutput, object, optional, picklist } from "valibot";
import { requireText, serviceAccount } from "../../../utils/validation/base";
import { MAX_CHARACTERS_SM } from "../../../constants";
import type { DataSourceDefinition } from "../../../types/DataSource";
import { resolveUpdateInterval, UPDATE_INTERVAL_OPTIONS, UPDATE_INTERVALS } from "../../../types/DataSource";
import { keyFromSelector } from "i18next";
import { useTranslation } from "../../../state/i18n";
import { TextInput } from "../../../components/inputs/TextInput";
import { Checkbox } from "../../../components/checkbox/Checkbox";
import ServiceAccountSelectInput from "../../../components/inputs/ServiceAccountSelectInput";
import { createUniqueId, Show } from "solid-js";
import FeatureService from "../../../services/FeatureService";
import { SingleSelectInput } from "../../../components/menu/Select";
import ServiceAccountService from "../../../services/ServiceAccountService";
import { isNil } from "lodash-es";
import ServiceAccountId from "../../../state/ServiceAccountId";
import { baseDataSourceSchema } from "../../../utils/validation/dataSource";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { RefreshDataSourceButton } from "./RefreshDataSourceButton";
import { Header } from "../../../components/inputs/Input";

const schema = object({
  ...baseDataSourceSchema.entries,
  serviceAccountId: optional(serviceAccount()), // TODO, DEV-7949 - Temporarily optional, due to existing data sources missing this property
  spreadSheetId: requireText(MAX_CHARACTERS_SM),
  updateInterval: picklist(UPDATE_INTERVALS),
});

export type GoogleSheetsDataSourceConfigurationDto = {
  googleConfiguration: {
    serviceAccountId?: string;
    spreadsheetId: string;
    updateInterval: string;
  };
};

const GoogleSheetsDataSource: DataSourceDefinition<
  InferOutput<typeof schema>,
  GoogleSheetsDataSourceConfigurationDto
> = {
  name: keyFromSelector(($) => $.DATASOURCES_GOOGLESHEETS_NAME),
  type: "GOOGLE_SPREADSHEET",
  icon: "googleSheetsLogo",
  schema,
  initialValue: {
    name: undefined,
    serviceAccountId: undefined,
    spreadSheetId: undefined,
    updateInterval: "NEVER",
    availableOffline: false,
  },
  toConfiguration: (input) => {
    if (isNil(input.googleConfiguration)) {
      throw new Error("Invalid datasource configuration");
    }

    return {
      name: input.name,
      serviceAccountId: !isNil(input.googleConfiguration.serviceAccountId)
        ? ServiceAccountId.from(input.googleConfiguration.serviceAccountId)
        : undefined,
      spreadSheetId: input.googleConfiguration.spreadsheetId,
      updateInterval: resolveUpdateInterval(input.googleConfiguration.updateInterval),
      availableOffline: input.searchType === "LOCAL_FALLBACK",
    };
  },
  toDto: (input) => {
    const { name, availableOffline, ...configuration } = input;

    return {
      name,
      googleConfiguration: {
        serviceAccountId: configuration.serviceAccountId?.toString(),
        spreadsheetId: configuration.spreadSheetId,
        updateInterval: configuration.updateInterval,
      },
      searchType: availableOffline ? "LOCAL_FALLBACK" : "LIVE",
    };
  },
  extraAction: (props) => <RefreshDataSourceButton {...props} />,
  EditItem: (props) => {
    const { t } = useTranslation();

    return (
      <div class="flex flex-col gap-6">
        <TextInput
          label={t(($) => $.DATASOURCE_INPUT_NAME)}
          value={props.value("name")}
          required
          onBlur={(e) => props.updateValue("name", e.target.value)}
          onInput={(e) => props.updateValue("name", e.target.value)}
          errorMessage={props.error("name")}
        />

        {/*
         * Some Google Sheets data sources use service accounts created and stored by the 'datasource' service itself.
         * These cannot be edited in the new platform until they have been migrated, see DEV-2218.
         */}
        <Show
          when={!(props.type === "EDIT" && isNil(props.value("serviceAccountId")))}
          fallback={
            <div class="flex flex-col gap-1">
              <Header
                id={createUniqueId()}
                label={t(($) => $.DATASOURCE_GOOGLE_SHEETS_INPUT_SERVICE_ACCOUNT)}
                required
              />
              <InlineMessage
                variant="informative"
                label={t(($) => $.DATASOURCE_GOOGLE_SHEETS_INPUT_SERVICE_ACCOUNT_LEGACY_MESSAGE)}
              />
            </div>
          }
        >
          <ServiceAccountSelectInput
            label={t(($) => $.DATASOURCE_GOOGLE_SHEETS_INPUT_SERVICE_ACCOUNT)}
            type="GOOGLE_SHEETS"
            required
            value={props.value("serviceAccountId") ?? null}
            fetchServiceAccounts={() => ServiceAccountService.getAll()}
            onChange={(serviceAccountId) => props.updateValue("serviceAccountId", serviceAccountId)}
            errorMessage={props.error("serviceAccountId")}
          />
        </Show>

        <TextInput
          label={t(($) => $.DATASOURCE_GOOGLE_SHEETS_INPUT_SPREADSHEET_ID)}
          value={props.value("spreadSheetId")}
          required
          onBlur={(e) => props.updateValue("spreadSheetId", e.target.value)}
          onInput={(e) => props.updateValue("spreadSheetId", e.target.value)}
          errorMessage={props.error("spreadSheetId")}
        />

        <Show when={FeatureService.hasFeature("AUTOMATIC_DATASOURCES")}>
          <SingleSelectInput
            label={t(($) => $.DATASOURCE_URL_INPUT_UPDATE_INTERVAL)}
            value={props.value("updateInterval")}
            onSelect={(value) => props.updateValue("updateInterval", value)}
            options={UPDATE_INTERVAL_OPTIONS.map(({ value, label }) => ({
              variant: "text",
              value,
              label: t(label),
            }))}
            sameWidth
            errorMessage={props.error("updateInterval")}
          />
        </Show>

        <Checkbox
          label={t(($) => $.DATASOURCE_INPUT_AVAILABLE_OFFLINE)}
          checked={props.value("availableOffline") === true}
          setChecked={(value) => props.updateValue("availableOffline", value)}
        />
      </div>
    );
  },
};

export default GoogleSheetsDataSource;
